/**
 * Precificação do item: resolve os parâmetros (padrão → licitação → item, D4/D22) e monta a entrada do motor v1.
 * Regras puras; o cálculo em si é calcularPreco (motor validado pelo gabarito de 39 casos).
 */
import Decimal from "decimal.js";
import { calcularPreco } from "./preco/motor";
import type { CondicaoPagamento, EntradaPreco, EntradaPrecoV1, FreteCompra, FreteEnvio, IcmsManual, RegraPreco, ResultadoPreco } from "./preco/tipos";

const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

export type OrigemParametro = "padrao" | "licitacao" | "item";
export type TipoFreteV1 = "sem_frete" | "cif_percentual" | "fob_estimado_pct";

export interface PadraoPreco {
  margem: number;
  seguro: number;
  custo_fixo: number;
  simples: number;
  frete_cif_pct: number;
}

/** Valores definidos na licitação ou no item; null/undefined = herda do nível acima. */
export interface ParametrosInformados {
  margem?: number | null;
  seguro?: number | null;
  custo_fixo?: number | null;
  simples?: number | null;
  frete_tipo?: TipoFreteV1 | null;
  frete_pct?: number | null;
}

export interface ParametrosResolvidos {
  margem: number;
  seguro: number;
  custo_fixo: number;
  simples: number;
  frete_tipo: TipoFreteV1 | null;
  frete_pct: number | null;
  origem: Record<"margem" | "seguro" | "custo_fixo" | "simples" | "frete", OrigemParametro>;
}

const definido = (v: number | null | undefined): v is number => v !== null && v !== undefined && Number.isFinite(v);

/**
 * Item → licitação → padrão. O frete não tem padrão de tipo (D22: escolha explícita); o percentual do CIF,
 * quando não informado, vem do padrão vigente. FOB estimado exige o percentual.
 */
export function resolverParametros(padrao: PadraoPreco, licitacao: ParametrosInformados, item: ParametrosInformados): ParametrosResolvidos {
  const origem = {} as ParametrosResolvidos["origem"];
  const escolher = (campo: "margem" | "seguro" | "custo_fixo" | "simples"): number => {
    const doItem = item[campo];
    if (definido(doItem)) {
      origem[campo] = "item";
      return doItem;
    }
    const daLicitacao = licitacao[campo];
    if (definido(daLicitacao)) {
      origem[campo] = "licitacao";
      return daLicitacao;
    }
    origem[campo] = "padrao";
    return padrao[campo];
  };
  const margem = escolher("margem");
  const seguro = escolher("seguro");
  const custo_fixo = escolher("custo_fixo");
  const simples = escolher("simples");

  let frete_tipo: TipoFreteV1 | null;
  let pctInformado: number | null | undefined;
  if (item.frete_tipo) {
    frete_tipo = item.frete_tipo;
    pctInformado = item.frete_pct;
    origem.frete = "item";
  } else if (licitacao.frete_tipo) {
    frete_tipo = licitacao.frete_tipo;
    pctInformado = licitacao.frete_pct;
    origem.frete = "licitacao";
  } else {
    frete_tipo = null;
    pctInformado = null;
    origem.frete = "padrao";
  }
  let frete_pct: number | null = null;
  if (frete_tipo === "sem_frete") frete_pct = 0;
  else if (frete_tipo === "cif_percentual") frete_pct = definido(pctInformado) ? pctInformado : padrao.frete_cif_pct;
  else if (frete_tipo === "fob_estimado_pct") frete_pct = definido(pctInformado) ? pctInformado : null;

  return { margem, seguro, custo_fixo, simples, frete_tipo, frete_pct, origem };
}

export interface ContextoPrecoV1 {
  custo_avista: number;
  /** Condição de pagamento do custo (D111); ausente = à vista. */
  pagamento?: CondicaoPagamento;
  uf_fornecedor: string;
  /** UF da empresa (destino da compra: a complementação de ICMS é na entrada em SE). */
  uf_empresa: string;
  informatica: boolean;
  /** Alíquota interna do destino (diagonal da matriz, com FECEP). */
  aliquota_interna: number;
  /** Alíquota interestadual da cotação; sem ela, a da matriz (origem → destino). */
  aliquota_interestadual: number;
  reducao_informatica: number;
  parametros: ParametrosResolvidos;
  icms_manual?: IcmsManual | null;
  valor_unit_edital?: number | null;
}

/** Entrada do motor v1 a partir do contexto do item. Erro se o frete ainda não foi escolhido (D22). */
export function entradaV1(c: ContextoPrecoV1): EntradaPrecoV1 {
  if (c.parametros.frete_tipo === null) throw new Error("Escolha o frete: sem frete, CIF em percentual ou FOB estimado em percentual.");
  if (c.parametros.frete_pct === null) throw new Error("Informe o percentual estimado do frete FOB.");
  return {
    regra: "v1-legado",
    custo_avista: c.custo_avista,
    ...(c.pagamento ? { pagamento: c.pagamento } : {}),
    uf_origem: c.uf_fornecedor,
    uf_destino: c.uf_empresa,
    informatica: c.informatica ? "S" : "N",
    aliquotas: { interna_destino: c.aliquota_interna, interestadual: c.aliquota_interestadual },
    reducao_informatica: c.reducao_informatica,
    markup: { margem: c.parametros.margem, seguro: c.parametros.seguro, custo_fixo: c.parametros.custo_fixo, simples: c.parametros.simples },
    frete_pct: c.parametros.frete_pct,
    ...(c.icms_manual ? { icms_manual: c.icms_manual } : {}),
    valor_unit_edital: c.valor_unit_edital ?? null,
  };
}

/** Calcula o preço do item pela regra v1. */
export function precificarV1(c: ContextoPrecoV1): ResultadoPreco {
  return calcularPreco(entradaV1(c));
}

/** ICMS negativo: na v1, compra dentro do estado de produto de informática dá −7% (erro da planilha, doc 13 item 1). */
export function icmsNegativo(resultado: Pick<ResultadoPreco, "icms_aplicado" | "icms_origem">): boolean {
  return resultado.icms_origem === "calculado" && resultado.icms_aplicado < 0;
}

/* ------------------------------------------------------------------------------------------------
 * Contexto único (v1-legado ou v2) usado pela tela de precificação (D87, D90).
 * ---------------------------------------------------------------------------------------------- */

export interface ContextoPreco {
  regra: RegraPreco;
  /** Custo unitário da compra na condição de pagamento escolhida. */
  custo_avista: number;
  /** Condição de pagamento do custo (D111); ausente = à vista. */
  pagamento?: CondicaoPagamento;
  uf_fornecedor: string;
  uf_empresa: string;
  /** Produto de informática do Anexo III (marcação do cadastro do produto, sugerida pelo NCM). */
  informatica: boolean;
  /** Alíquota interna geral do destino, com FECEP (diagonal da matriz). */
  aliquota_interna: number;
  /** v2: alíquota interna de informática do destino, com FECEP (parâmetro fiscal interna_informatica). */
  aliquota_interna_informatica: number;
  /** v1: redução de informática da planilha. */
  reducao_informatica: number;
  /**
   * Alíquota interestadual da NF/cotação (ou escolhida no item). Na v1, sem ela o chamador passa a da matriz;
   * na v2 é obrigatória em compra de outro estado (não se deduz: 4% importado, 7% ou 12%).
   */
  aliquota_interestadual: number | null;
  /** v2: ST já incluído no custo. */
  st_incluso: boolean;
  /** v2: IPI por unidade pago na compra. */
  ipi_valor_unit: number;
  /** v3: adicional do FECOEP (parâmetro fiscal fecoep, SE: 0.01). */
  fecoep: number;
  /** v3: material escolar do art. 616-C-A, IV, "c" (sem FECOEP). */
  fecoep_isento: boolean;
  /** v3: redução da base do ICMS na NF de compra (CST 20), fração. */
  reducao_base: number;
  /** v2: frete do produto (compra, D96). null = ainda não informado (a cotação não diz e o item não escolheu). */
  frete_compra: FreteCompra | null;
  parametros: ParametrosResolvidos;
  icms_manual?: IcmsManual | null;
  valor_unit_edital?: number | null;
}

/** Frete de envio ao órgão na v2 (D96): sem frete ou percentual no divisor. */
export function freteEnvioV2(p: ParametrosResolvidos): FreteEnvio {
  if (p.frete_tipo === null) throw new Error("Escolha o frete de envio: sem frete ou percentual.");
  if (p.frete_tipo === "sem_frete") return { tipo: "sem_frete" };
  if (p.frete_tipo === "fob_estimado_pct") throw new Error("Frete de envio: escolha sem frete ou percentual (o FOB é frete do produto).");
  if (p.frete_pct === null) throw new Error("Informe o percentual do frete de envio.");
  return { tipo: "percentual", pct: p.frete_pct };
}

export function entradaPreco(c: ContextoPreco): EntradaPreco {
  if (c.regra === "v1-legado") {
    if (c.aliquota_interestadual === null) throw new Error("Sem a alíquota interestadual da cotação nem da matriz.");
    return entradaV1({ ...c, aliquota_interestadual: c.aliquota_interestadual });
  }
  const p = c.parametros;
  const comum = {
    custo_avista: c.custo_avista,
    ...(c.pagamento ? { pagamento: c.pagamento } : {}),
    uf_origem: c.uf_fornecedor,
    uf_destino: c.uf_empresa,
    informatica_anexo_iii: c.informatica,
    interna_destino_informatica: c.aliquota_interna_informatica,
    interna_destino: c.aliquota_interna,
    interestadual_nf: c.aliquota_interestadual,
    st_incluso: c.st_incluso,
    markup: { margem: p.margem, seguro: p.seguro, custo_fixo: p.custo_fixo, simples: p.simples },
    frete_compra: exigirFreteCompra(c.frete_compra),
    frete_envio: freteEnvioV2(p),
    ipi_valor_unit: c.ipi_valor_unit,
    ...(c.icms_manual ? { icms_manual: c.icms_manual } : {}),
    valor_unit_edital: c.valor_unit_edital ?? null,
  };
  if (c.regra === "v2") return { regra: "v2", ...comum };
  return { regra: "v3", ...comum, fecoep: c.fecoep, fecoep_isento: c.fecoep_isento, reducao_base: c.reducao_base };
}

function exigirFreteCompra(f: FreteCompra | null): FreteCompra {
  if (!f) throw new Error("Informe o frete do produto: incluso no preço (CIF), FOB em R$ ou FOB estimado em %.");
  return f;
}

/** Calcula o preço do item pela regra do contexto. */
export function precificar(c: ContextoPreco): ResultadoPreco {
  return calcularPreco(entradaPreco(c));
}

/**
 * Cálculo reverso: a margem que leva ao preço unitário desejado, mantidos os demais parâmetros.
 * A margem tem 6 casas (como no banco); entre as vizinhas, escolhe a que reproduz exatamente o preço.
 */
export function margemParaPreco(c: ContextoPreco, precoAlvo: number): { margem: number; resultado: ResultadoPreco } {
  if (!Number.isFinite(precoAlvo) || precoAlvo <= 0) throw new Error("Informe um preço maior que zero.");
  const atual = precificar(c);
  // preço = base ÷ divisor; a margem entra no divisor: mudar a margem em Δ muda o divisor em −Δ.
  const base = new D(atual.preco_unit_exato).div(atual.markup);
  const divisorAtual = new D(1).div(atual.markup);
  const divisorAlvo = base.div(precoAlvo);
  const exata = new D(c.parametros.margem).plus(divisorAtual.minus(divisorAlvo));
  const pct = exata.times(100).toDecimalPlaces(2).toString().replace(".", ",");
  if (exata.lt(0)) throw new Error(`Com esse preço a margem ficaria negativa (${pct}%).`);
  if (exata.gte(1)) throw new Error("Preço impossível com os demais percentuais.");
  const arredondada = exata.toDecimalPlaces(6).toNumber();
  const tentar = (m: number): ResultadoPreco | null => {
    if (m < 0 || m >= 1) return null;
    try {
      return precificar({ ...c, parametros: { ...c.parametros, margem: m } });
    } catch {
      return null;
    }
  };
  for (let k = 0; k <= 200; k++) {
    for (const m of k === 0 ? [arredondada] : [arredondada + k * 1e-6, arredondada - k * 1e-6]) {
      const margem = Math.round(m * 1e6) / 1e6;
      const r = tentar(margem);
      if (r && r.preco_unit === Math.round(precoAlvo * 100) / 100) return { margem, resultado: r };
    }
  }
  const r = tentar(arredondada);
  if (!r) throw new Error("Não foi possível calcular a margem para esse preço.");
  return { margem: arredondada, resultado: r };
}
